import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, Clock, Loader2, LogIn, Search, Send, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getAllCamerasWithSlugs, type CameraPageData } from '@/lib/cameraSlug';
import { getAllBrands, generateBrandSlug, type BrandSummary } from '@/lib/brandSlug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  PanelChrome, ModelRow, draftFromCamera, buildCorrections, specSnapshot, errCodeOf,
  type CorrectionDraft, type Verdict, type VerdictRow,
} from './ManufacturerVerifyPanel';

/**
 * Open verification flow, /verify (no token).
 *
 * Access model chosen deliberately against scraping: the whole route
 * requires a logged-in account, and the brand review workspace (model
 * list + specs) opens only after the founder approves this user's
 * access request for that specific brand. Requesting access emails the
 * founder the user's details with single-use Accept/Reject links
 * (moderate-brand-access edge fn); the user is emailed the decision.
 *
 * Once approved, verdicts save to the server as the user works (same
 * mechanics as the invited /verify/<token> panel, whose components are
 * reused here) and nothing auto-merges into the catalog.
 */

type AccessStatus = 'unknown' | 'none' | 'pending' | 'rejected' | 'approved';

const PAGE_SIZE = 50;

export default function OpenVerifyPanel() {
  const { user, loading: authLoading } = useAuth();

  const [brand, setBrand] = useState<BrandSummary | null>(null);
  const [brandSearch, setBrandSearch] = useState('');

  const [accessStatus, setAccessStatus] = useState<AccessStatus>('unknown');
  const [accessError, setAccessError] = useState(false);
  const [accessTick, setAccessTick] = useState(0);
  const [requestMessage, setRequestMessage] = useState('');
  const [requesting, setRequesting] = useState(false);

  const [verdictMap, setVerdictMap] = useState<Map<string, VerdictRow>>(new Map());
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'all' | 'pending' | 'reviewed'>('all');
  const [page, setPage] = useState(1);
  const [savingModels, setSavingModels] = useState<Set<string>>(new Set());
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [revisingModels, setRevisingModels] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<CorrectionDraft | null>(null);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  const brands = useMemo(() => getAllBrands(), []);
  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    return q ? brands.filter(b => b.name.toLowerCase().includes(q)) : brands;
  }, [brands, brandSearch]);

  // Model list only materializes once access is approved: before that
  // this component renders nothing but brand names and counts.
  const models = useMemo(() => {
    if (!brand || accessStatus !== 'approved') return [] as CameraPageData[];
    return getAllCamerasWithSlugs()
      .filter(c => generateBrandSlug(c.manufacturer) === brand.slug)
      .sort((a, b) => a.model.localeCompare(b.model));
  }, [brand, accessStatus]);

  const invoke = useCallback(async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('manufacturer-panel', { body });
    if (error) throw new Error(await errCodeOf(error));
    return (data ?? {}) as Record<string, unknown>;
  }, []);

  // Access status for the picked brand. Depends on the stable user id,
  // not the user object: the hourly TOKEN_REFRESHED event mints a fresh
  // object reference and must not blank an open workspace.
  const userId = user?.id;
  useEffect(() => {
    if (!brand || !userId) return;
    let cancelled = false;
    setAccessStatus('unknown');
    setAccessError(false);
    (async () => {
      try {
        const res = await invoke({ action: 'access_status', brand_slug: brand.slug });
        if (cancelled) return;
        const status = String(res.status ?? 'none') as AccessStatus;
        if (status === 'approved' && Array.isArray(res.verdicts)) {
          const rows = res.verdicts as VerdictRow[];
          setVerdictMap(new Map(rows.map(v => [v.model_name, v])));
        }
        setAccessStatus(status);
      } catch {
        // Stay on this brand and offer a retry; ejecting to the picker
        // would discard the reviewer's context over a network blip.
        if (!cancelled) setAccessError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [brand, userId, invoke, accessTick]);

  // Full reset: nothing from a previously viewed brand may leak into
  // the next one (verdict counts, filters, stale approved status).
  const pickBrand = (b: BrandSummary) => {
    setBrand(b);
    setAccessStatus('unknown');
    setAccessError(false);
    setVerdictMap(new Map());
    setSearch('');
    setTab('all');
    setPage(1);
    setEditingModel(null);
    setRevisingModels(new Set());
    setDraft(null);
    setRequestMessage('');
    setReportSent(false);
  };

  const leaveBrand = () => {
    setBrand(null);
    setAccessStatus('unknown');
    setAccessError(false);
    setVerdictMap(new Map());
  };

  const requestAccess = async () => {
    if (!brand) return;
    setRequesting(true);
    try {
      const res = await invoke({
        action: 'request_access',
        brand_slug: brand.slug,
        brand_name: brand.name,
        message: requestMessage.trim() || undefined,
      });
      setAccessStatus(String(res.status ?? 'pending') as AccessStatus);
      toast.success('Request sent. You will get an email once it is reviewed.');
    } catch (e) {
      const code = e instanceof Error ? e.message : 'unknown';
      toast.error(
        code === 'too_many_requests'
          ? 'Too many requests from this account. Please try again later.'
          : 'Sending the request failed. Please try again.',
      );
    } finally {
      setRequesting(false);
    }
  };

  const markSaving = (names: string[], on: boolean) => {
    setSavingModels(prev => {
      const next = new Set(prev);
      for (const n of names) { if (on) next.add(n); else next.delete(n); }
      return next;
    });
  };

  const submitVerdicts = useCallback(
    async (items: Array<{ model_name: string; verdict: Verdict; corrections?: Record<string, unknown>; source_url?: string; note?: string; baseline?: Record<string, unknown> }>) => {
      if (!brand) return false;
      const names = items.map(i => i.model_name);
      markSaving(names, true);
      try {
        await invoke({ action: 'user_submit', brand_slug: brand.slug, items });
        const now = new Date().toISOString();
        setVerdictMap(prev => {
          const next = new Map(prev);
          for (const i of items) {
            next.set(i.model_name, {
              model_name: i.model_name,
              verdict: i.verdict,
              corrections: i.corrections ?? null,
              source_url: i.source_url ?? null,
              note: i.note ?? null,
              updated_at: now,
            });
          }
          return next;
        });
        setRevisingModels(prev => {
          const next = new Set(prev);
          for (const n of names) next.delete(n);
          return next;
        });
        return true;
      } catch (e) {
        const code = e instanceof Error ? e.message : 'unknown';
        if (code === 'not_approved' || code === 'auth_required') {
          setAccessStatus('unknown');
          setAccessTick(t => t + 1);
          toast.error('Your access could not be confirmed. Re-checking...');
        } else {
          toast.error('Saving failed. Please check your connection and try again.');
        }
        return false;
      } finally {
        markSaving(names, false);
      }
    },
    [brand, invoke],
  );

  const openCorrection = (cam: CameraPageData) => {
    setEditingModel(cam.model);
    const existing = verdictMap.get(cam.model);
    const base = draftFromCamera(cam);
    if (existing?.verdict === 'corrected' && existing.corrections) {
      const c = existing.corrections;
      setDraft({
        ...base,
        hfov: c.hfov != null ? String(c.hfov) : base.hfov,
        range: c.range != null ? String(c.range) : base.range,
        irRange: c.irRange != null ? String(c.irRange) : base.irRange,
        resolution: typeof c.resolution === 'string' ? c.resolution : base.resolution,
        focalLength: typeof c.focalLength === 'string' ? c.focalLength : base.focalLength,
        type: typeof c.type === 'string' ? c.type : base.type,
        sourceUrl: existing.source_url ?? '',
        note: existing.note ?? '',
      });
    } else {
      setDraft(base);
    }
  };

  const saveCorrection = (cam: CameraPageData) => {
    if (!draft) return;
    const built = buildCorrections(cam, draft);
    if ('error' in built) {
      toast.error(built.error);
      return;
    }
    const sourceUrl = draft.sourceUrl.trim();
    if (!/^https?:\/\//i.test(sourceUrl) || sourceUrl.length > 500) {
      toast.error('A datasheet or product page URL is required (max 500 characters).');
      return;
    }
    const note = draft.note.trim();
    if (note.length > 2000) {
      toast.error('The note is too long (max 2000 characters).');
      return;
    }
    void submitVerdicts([{
      model_name: cam.model,
      verdict: 'corrected',
      corrections: built.corrections,
      source_url: sourceUrl,
      note: note || undefined,
      baseline: specSnapshot(cam),
    }]).then(ok => {
      if (ok) { setEditingModel(null); setDraft(null); }
    });
  };

  const handleFinalize = async () => {
    if (!brand) return;
    setFinalizing(true);
    try {
      await invoke({ action: 'user_finalize', brand_slug: brand.slug });
      setReportSent(true);
      setFinalizeOpen(false);
      toast.success('Report submitted. Thank you for verifying your data.');
    } catch (e) {
      const code = e instanceof Error ? e.message : 'unknown';
      if (code === 'Rate limit exceeded') {
        toast.error('Report limit reached (3 per hour). Your answers are saved; please try again later.');
      } else if (code === 'not_approved' || code === 'auth_required') {
        setFinalizeOpen(false);
        setAccessStatus('unknown');
        setAccessTick(t => t + 1);
        toast.error('Your access could not be confirmed. Re-checking...');
      } else {
        toast.error('Submitting the report failed. Your per-model answers are saved; please try again.');
      }
    } finally {
      setFinalizing(false);
    }
  };

  const counts = useMemo(() => {
    const c = { confirmed: 0, corrected: 0, not_ours: 0, discontinued: 0 };
    for (const v of verdictMap.values()) c[v.verdict] += 1;
    const reviewed = c.confirmed + c.corrected + c.not_ours + c.discontinued;
    return { ...c, reviewed, pending: Math.max(0, models.length - reviewed) };
  }, [verdictMap, models.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models.filter(m => {
      if (q && !m.model.toLowerCase().includes(q)) return false;
      const v = verdictMap.get(m.model);
      if (tab === 'pending') return !v;
      if (tab === 'reviewed') return Boolean(v);
      return true;
    });
  }, [models, search, tab, verdictMap]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageModels = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pendingOnPage = pageModels.filter(m => !verdictMap.get(m.model)).length;

  // ---- Auth gate ----------------------------------------------------

  if (authLoading) {
    return (
      <PanelChrome>
        <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-sm">Checking your account...</p>
        </div>
      </PanelChrome>
    );
  }

  if (!user) {
    return (
      <PanelChrome>
        <div className="max-w-md mx-auto py-24 px-4">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Manufacturer verification
          </p>
          <h1 className="text-xl font-semibold mb-3">Sign in to continue</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Reviewing catalog specifications requires a CCTVplanner account. Sign in, pick your
            brand and request access; we approve requests manually and email you the decision.
          </p>
          <Button asChild className="w-full h-10 gap-1.5">
            <Link to="/auth">
              <LogIn className="w-4 h-4" />
              Sign in or create an account
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground mt-6">
            Tip: an account registered with your official company email address
            (you@yourbrand.com) speeds up approval.
          </p>
        </div>
      </PanelChrome>
    );
  }

  // ---- Brand picker -------------------------------------------------

  if (!brand) {
    return (
      <PanelChrome>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Manufacturer verification
          </p>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3">
            Verify the specifications we publish for your cameras
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl mb-8">
            Pick your brand and request access to its review workspace. We approve every request
            manually, usually within 2 business days, and email you the decision; approved reviewers can confirm or correct each
            model (a datasheet link is required) and nothing goes live without our editorial check.
          </p>
          <div className="relative mb-6 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={brandSearch}
              onChange={e => setBrandSearch(e.target.value)}
              placeholder="Search your brand..."
              className="pl-9 h-10"
            />
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {filteredBrands.slice(0, 60).map(b => (
              <li key={b.slug}>
                <button
                  type="button"
                  onClick={() => pickBrand(b)}
                  className="w-full text-left px-3 py-2.5 border border-border hover:border-foreground transition-colors"
                >
                  <span className="text-sm font-medium block truncate">{b.name}</span>
                  <span className="text-xs text-muted-foreground">{b.cameraCount.toLocaleString()} models</span>
                </button>
              </li>
            ))}
          </ul>
          {filteredBrands.length === 0 && (
            <p className="text-sm text-muted-foreground py-8">
              No brand matches. If your brand is missing from the catalog, write to{' '}
              <a href="mailto:contact@cctvplanner.io" className="underline underline-offset-4">contact@cctvplanner.io</a>.
            </p>
          )}
        </div>
      </PanelChrome>
    );
  }

  // ---- Access states ------------------------------------------------

  if (accessStatus === 'unknown') {
    return (
      <PanelChrome brandName={brand.name}>
        <div className="max-w-md mx-auto py-32 px-4 text-center">
          {accessError ? (
            <>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Connection</p>
              <h1 className="text-xl font-semibold mb-3">Checking your access failed</h1>
              <p className="text-sm text-muted-foreground mb-6">
                This looks like a temporary problem. Your saved verdicts are safe.
              </p>
              <div className="flex flex-col items-center gap-3">
                <Button className="h-10 px-6" onClick={() => setAccessTick(t => t + 1)}>Try again</Button>
                <button
                  type="button"
                  onClick={leaveBrand}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Choose a different brand
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              <p className="text-sm">Checking your access to {brand.name}...</p>
            </div>
          )}
        </div>
      </PanelChrome>
    );
  }

  if (accessStatus !== 'approved') {
    return (
      <PanelChrome brandName={brand.name}>
        <div className="max-w-md mx-auto py-20 px-4">
          <button
            type="button"
            onClick={leaveBrand}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Choose a different brand
          </button>

          {accessStatus === 'none' && (
            <>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                Access request
              </p>
              <h1 className="text-xl font-semibold mb-3">
                Request access to the {brand.name} workspace
              </h1>
              <p className="text-sm text-muted-foreground mb-4">
                We grant review access per brand, manually. Your request goes straight to our
                team with your account details ({user.email}), and you will get the decision
                by email.
              </p>
              <div className="border border-border bg-muted/30 p-3 text-xs leading-relaxed mb-4">
                <strong>Faster approval:</strong> request from an account registered with your
                official company address (you@yourbrand.com), and mention your role at the
                brand below.
              </div>
              <label className="text-xs font-medium block mb-1" htmlFor="access-message">
                Who are you at {brand.name}? (optional)
              </label>
              <Textarea
                id="access-message"
                value={requestMessage}
                onChange={e => setRequestMessage(e.target.value)}
                placeholder="e.g. Product manager, EMEA. I maintain our datasheets."
                rows={3}
                className="text-sm mb-4"
              />
              <Button className="w-full h-10 gap-1.5" onClick={requestAccess} disabled={requesting}>
                {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Request access
              </Button>
            </>
          )}

          {accessStatus === 'pending' && (
            <>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                Access request
              </p>
              <h1 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Request under review
              </h1>
              <p className="text-sm text-muted-foreground">
                Your request for the {brand.name} workspace was sent to our team. We review
                requests manually, usually within 2 business days; the decision lands at{' '}
                <strong>{user.email}</strong>.
              </p>
            </>
          )}

          {accessStatus === 'rejected' && (
            <>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                Access request
              </p>
              <h1 className="text-xl font-semibold mb-3">Access was not granted</h1>
              <p className="text-sm text-muted-foreground">
                Your request for the {brand.name} workspace was declined. If you can verify your
                affiliation with the brand (for example an official company email address),
                write to{' '}
                <a href="mailto:contact@cctvplanner.io" className="underline underline-offset-4">
                  contact@cctvplanner.io
                </a>.
              </p>
            </>
          )}
        </div>
      </PanelChrome>
    );
  }

  // ---- Approved workspace -------------------------------------------

  const progressPct = models.length > 0 ? Math.round((counts.reviewed / models.length) * 100) : 0;

  const tabs: Array<{ key: typeof tab; label: string; count: number }> = [
    { key: 'all', label: 'All', count: models.length },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'reviewed', label: 'Reviewed', count: counts.reviewed },
  ];

  return (
    <PanelChrome brandName={brand.name} company={user.email ?? undefined}>
      <div className="max-w-5xl mx-auto px-4 pb-40">
        <section className="py-8 border-b border-border">
          <button
            type="button"
            onClick={leaveBrand}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Choose a different brand
          </button>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Manufacturer verification
          </p>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3">
            {brand.name}: {models.length.toLocaleString()} models in the CCTVplanner catalog
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Confirm what is right, correct what is wrong (a datasheet link is required), and
            flag anything that is not your product or no longer sold. Every answer is saved
            instantly; nothing goes live without our editorial review.
          </p>
        </section>

        <section className="py-5 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {counts.reviewed.toLocaleString()} of {models.length.toLocaleString()} models reviewed
            </span>
            <span className="text-sm text-muted-foreground">{progressPct}%</span>
          </div>
          <div className="h-2 bg-muted overflow-hidden">
            <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          {reportSent && (
            <p className="mt-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              A report has been submitted to CCTVplanner. You can keep reviewing and submit again.
            </p>
          )}
        </section>

        <section className="py-4 flex flex-col md:flex-row md:items-center gap-3 sticky top-0 bg-background z-10 border-b border-border">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search model number..."
              className="pl-9 h-9 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setTab(t.key); setPage(1); }}
                className={
                  t.key === tab
                    ? 'px-3 py-1.5 text-xs font-medium border border-foreground bg-foreground text-background transition-colors'
                    : 'px-3 py-1.5 text-xs font-medium border border-border text-muted-foreground hover:border-foreground transition-colors'
                }
              >
                {t.label} <span className="opacity-60">({t.count.toLocaleString()})</span>
              </button>
            ))}
          </div>
        </section>

        {pendingOnPage > 1 && (
          <div className="py-3 flex justify-end">
            <Button
              variant="outline" size="sm" className="h-8 text-xs gap-1.5"
              onClick={() => {
                const items = pageModels
                  .filter(m => !verdictMap.get(m.model))
                  .map(m => ({ model_name: m.model, verdict: 'confirmed' as Verdict, baseline: specSnapshot(m) }));
                void submitVerdicts(items).then(ok => {
                  if (ok) toast.success(`Confirmed ${items.length} models`);
                });
              }}
            >
              <Check className="w-3.5 h-3.5" />
              Confirm all {pendingOnPage} pending on this page
            </Button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted-foreground">
            No models match the current search and filter.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {pageModels.map(cam => (
              <ModelRow
                key={cam.slug}
                cam={cam}
                verdict={verdictMap.get(cam.model) ?? null}
                saving={savingModels.has(cam.model)}
                editing={editingModel === cam.model}
                revising={revisingModels.has(cam.model)}
                draft={editingModel === cam.model ? draft : null}
                onDraftChange={setDraft}
                onConfirm={() => void submitVerdicts([{ model_name: cam.model, verdict: 'confirmed', baseline: specSnapshot(cam) }])}
                onFlag={(v) => void submitVerdicts([{ model_name: cam.model, verdict: v, baseline: specSnapshot(cam) }])}
                onOpenCorrection={() => openCorrection(cam)}
                onCancelCorrection={() => { setEditingModel(null); setDraft(null); }}
                onSaveCorrection={() => saveCorrection(cam)}
                onRevise={() => setRevisingModels(prev => new Set(prev).add(cam.model))}
              />
            ))}
          </ul>
        )}

        {pageCount > 1 && (
          <nav className="flex items-center justify-between py-6" aria-label="Pagination">
            <Button
              variant="outline" size="sm" className="h-8 text-xs"
              disabled={safePage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {safePage} of {pageCount} · {filtered.length.toLocaleString()} models
            </span>
            <Button
              variant="outline" size="sm" className="h-8 text-xs"
              disabled={safePage >= pageCount}
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
            >
              Next
            </Button>
          </nav>
        )}
      </div>

      {counts.reviewed > 0 && (
        <div className="fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-20">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground hidden sm:block">
              {counts.confirmed} confirmed · {counts.corrected} corrected · {counts.not_ours + counts.discontinued} flagged
            </p>
            <Button size="sm" className="h-9 text-xs gap-1.5 ml-auto" onClick={() => setFinalizeOpen(true)}>
              <Send className="w-3.5 h-3.5" />
              Submit report to CCTVplanner
            </Button>
          </div>
        </div>
      )}

      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit your verification report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-muted-foreground">
              We will receive a summary of your review ({counts.reviewed.toLocaleString()} models:
              {' '}{counts.confirmed} confirmed, {counts.corrected} corrections,
              {' '}{counts.not_ours + counts.discontinued} flagged) and apply the changes after
              editorial review. We will reach you at {user.email} with any questions.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setFinalizeOpen(false)} disabled={finalizing}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleFinalize} disabled={finalizing} className="gap-1.5">
              {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PanelChrome>
  );
}
